import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface ThemeColors {
  primary: string; // Sidebar, Navbar, buttons - gradient start
  secondary: string; // Gradient end
  tertiary: string; // Hover states
  background: string; // App background
  name: string; // Theme name
}

export interface ThemePreset {
  id: string;
  name: string;
  nameKey: string; // Translation key
  colors: ThemeColors;
}

// Preset themes
export const themePresets: ThemePreset[] = [
  {
    id: 'default',
    name: 'Default',
    nameKey: 'settings.themeDefault',
    colors: {
      primary: '#0f766e', // teal-800
      secondary: '#0891b2', // cyan-600
      tertiary: '#0e7490', // cyan-700
      background: '#f3f4f6', // gray-100
      name: 'Default'
    }
  },
  {
    id: 'ocean',
    name: 'Ocean Blue',
    nameKey: 'settings.themeOcean',
    colors: {
      primary: '#1e40af', // blue-800
      secondary: '#3b82f6', // blue-500
      tertiary: '#2563eb', // blue-600
      background: '#f0f9ff', // sky-50
      name: 'Ocean Blue'
    }
  },
  {
    id: 'purple',
    name: 'Purple',
    nameKey: 'settings.themePurple',
    colors: {
      primary: '#6b21a8', // purple-800
      secondary: '#a855f7', // purple-500
      tertiary: '#7c3aed', // violet-600
      background: '#faf5ff', // purple-50
      name: 'Purple'
    }
  },
  {
    id: 'emerald',
    name: 'Emerald',
    nameKey: 'settings.themeEmerald',
    colors: {
      primary: '#065f46', // emerald-800
      secondary: '#10b981', // emerald-500
      tertiary: '#059669', // emerald-600
      background: '#ecfdf5', // emerald-50
      name: 'Emerald'
    }
  },
  {
    id: 'rose',
    name: 'Rose',
    nameKey: 'settings.themeRose',
    colors: {
      primary: '#9f1239', // rose-800
      secondary: '#f43f5e', // rose-500
      tertiary: '#e11d48', // rose-600
      background: '#fff1f2', // rose-50
      name: 'Rose'
    }
  },
  {
    id: 'slate',
    name: 'Slate',
    nameKey: 'settings.themeSlate',
    colors: {
      primary: '#1e293b', // slate-800
      secondary: '#475569', // slate-600
      tertiary: '#334155', // slate-700
      background: '#f8fafc', // slate-50
      name: 'Slate'
    }
  },
  {
    id: 'yellow',
    name: 'Yellow',
    nameKey: 'settings.themeYellow',
    colors: {
      primary: '#854d0e', // yellow-800
      secondary: '#eab308', // yellow-500
      tertiary: '#ca8a04', // yellow-600
      background: '#fefce8', // yellow-50
      name: 'Yellow'
    }
  },
  {
    id: 'orange',
    name: 'Orange',
    nameKey: 'settings.themeOrange',
    colors: {
      primary: '#9a3412', // orange-800
      secondary: '#f97316', // orange-500
      tertiary: '#ea580c', // orange-600
      background: '#fff7ed', // orange-50
      name: 'Orange'
    }
  }
];

interface ThemeContextType {
  mode: 'light' | 'dark';
  colors: ThemeColors;
  currentPresetId: string;
  setMode: (mode: 'light' | 'dark') => void;
  setColors: (colors: ThemeColors) => void;
  setPreset: (presetId: string) => void;
  applyTheme: (presetId: string, mode: 'light' | 'dark', colors: ThemeColors) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const defaultColors = themePresets[0].colors;

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('themeMode');
    return (saved as 'light' | 'dark') || 'light';
  });

  const [currentPresetId, setCurrentPresetId] = useState<string>(() => {
    return localStorage.getItem('themePresetId') || 'default';
  });

  const [colors, setColors] = useState<ThemeColors>(() => {
    const savedPresetId = localStorage.getItem('themePresetId');
    if (savedPresetId) {
      const preset = themePresets.find(p => p.id === savedPresetId);
      if (preset) return preset.colors;
    }
    const saved = localStorage.getItem('themeColors');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return defaultColors;
      }
    }
    return defaultColors;
  });

  useEffect(() => {
    // Apply theme mode
    const root = document.documentElement;
    if (mode === 'dark') {
      root.classList.add('dark');
      // Dark mode: dark background, light text
      root.style.setProperty('--bg-color', '#1f2937'); // gray-800
      root.style.setProperty('--text-color', '#f9fafb'); // gray-50
      root.style.setProperty('--bg-secondary', '#374151'); // gray-700
      root.style.setProperty('--text-secondary', '#e5e7eb'); // gray-200
      root.style.setProperty('--border-color', '#4b5563'); // gray-600
      document.body.style.backgroundColor = '#1f2937';
      document.body.style.color = '#f9fafb';
    } else {
      root.classList.remove('dark');
      // Light mode: light background, dark text
      root.style.setProperty('--bg-color', '#ffffff');
      root.style.setProperty('--text-color', '#111827'); // gray-900
      root.style.setProperty('--bg-secondary', '#f9fafb'); // gray-50
      root.style.setProperty('--text-secondary', '#6b7280'); // gray-500
      root.style.setProperty('--border-color', '#e5e7eb'); // gray-200
      document.body.style.backgroundColor = colors.background;
      document.body.style.color = '#111827';
    }
    localStorage.setItem('themeMode', mode);
  }, [mode, colors.background]);

  useEffect(() => {
    // Apply theme colors as CSS variables
    const root = document.documentElement;
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-tertiary', colors.tertiary);
    
    // Background color varies by mode
    if (mode === 'light') {
      root.style.setProperty('--color-background', colors.background);
      document.body.style.backgroundColor = colors.background;
    } else {
      // In dark mode, use dark background but keep theme background for specific elements
      root.style.setProperty('--color-background', '#374151'); // gray-700 for dark mode backgrounds
    }
    
    // Convert hex to RGB for use in gradients with opacity
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : null;
    };

    const primaryRgb = hexToRgb(colors.primary);
    const secondaryRgb = hexToRgb(colors.secondary);
    const tertiaryRgb = hexToRgb(colors.tertiary);
    const backgroundRgb = hexToRgb(colors.background);

    if (primaryRgb) root.style.setProperty('--color-primary-rgb', primaryRgb);
    if (secondaryRgb) root.style.setProperty('--color-secondary-rgb', secondaryRgb);
    if (tertiaryRgb) root.style.setProperty('--color-tertiary-rgb', tertiaryRgb);
    if (backgroundRgb) root.style.setProperty('--color-background-rgb', backgroundRgb);

    localStorage.setItem('themeColors', JSON.stringify(colors));
  }, [colors, mode]);

  const handleSetColors = (newColors: ThemeColors) => {
    setColors(newColors);
  };

  const handleSetPreset = (presetId: string) => {
    const preset = themePresets.find(p => p.id === presetId);
    if (preset) {
      setCurrentPresetId(presetId);
      setColors(preset.colors);
      localStorage.setItem('themePresetId', presetId);
    }
  };

  const applyTheme = (presetId: string, newMode: 'light' | 'dark', newColors: ThemeColors) => {
    setCurrentPresetId(presetId);
    setMode(newMode);
    setColors(newColors);
    localStorage.setItem('themePresetId', presetId);
    localStorage.setItem('themeMode', newMode);
    localStorage.setItem('themeColors', JSON.stringify(newColors));
  };

  return (
    <ThemeContext.Provider
      value={{
        mode,
        colors,
        currentPresetId,
        setMode,
        setColors: handleSetColors,
        setPreset: handleSetPreset,
        applyTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
